
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Package, AlertTriangle, Edit, Trash2, ZoomIn } from "lucide-react";

interface Product {
  id: string;
  barcode: string;
  name: string;
  boxes: number;
  unitsPerBox: number;
  photos: string[];
  hasCredit: boolean;
  posStatus?: 'found' | 'not-found';
  catalogName?: string;
}

interface ProductListTableProps {
  products: Product[];
  onProductClick: (product: Product) => void;
  onEditProduct: (product: Product) => void;
  onDeleteProduct: (product: Product) => void;
  onImageClick: (imageUrl: string, e: React.MouseEvent) => void;
}

export const ProductListTable = ({
  products,
  onProductClick,
  onEditProduct,
  onDeleteProduct,
  onImageClick
}: ProductListTableProps) => {
  const getFirstPhoto = (p: any): string | null => {
    const candidates: any = p?.photos ?? p?.photo ?? p?.photoUrl ?? p?.image_url ?? p?.imageUrl ?? null;
    let arr: string[] = [];

    if (Array.isArray(candidates)) {
      arr = candidates.filter((s: any) => typeof s === 'string' && s.length > 0);
    } else if (typeof candidates === 'string') {
      const str = candidates.trim();
      if (str.startsWith('[')) {
        try {
          const parsed = JSON.parse(str);
          if (Array.isArray(parsed)) {
            arr = parsed.filter((s: any) => typeof s === 'string' && s.length > 0);
          }
        } catch {
          arr = [str];
        }
      } else if (str.includes(',')) {
        arr = str.split(',').map(s => s.trim()).filter(Boolean);
      } else if (str.length > 0) {
        arr = [str];
      }
    } else if (candidates && typeof candidates === 'object') {
      if (Array.isArray(candidates)) {
        arr = (candidates as any[])
          .map((it: any) => (typeof it === 'string' ? it : it?.url))
          .filter((s: any) => typeof s === 'string' && s.length > 0);
      } else if (typeof (candidates as any).url === 'string') {
        arr = [(candidates as any).url];
      }
    }

    const filtered = arr
      .map((u) => (typeof u === 'string' ? u.trim() : u))
      .filter((u): u is string => !!u && !u.startsWith('blob:'));

    return filtered.length > 0 ? filtered[0] : null;
  };

  return (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Image</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Barcode</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Boxes</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Units/Box</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {products.map((product) => (
                <tr 
                  key={product.id} 
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => onProductClick(product)}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex gap-1">
                      {product.posStatus === 'found' ? (
                        <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">✓</span>
                        </div>
                      ) : (
                        <div className="w-4 h-4 bg-yellow-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">+</span>
                        </div>
                      )}
                      {product.hasCredit && (
                        <AlertTriangle className="w-4 h-4 text-orange-500" />
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {(() => {
                      const firstPhoto = getFirstPhoto(product);
                      return firstPhoto ? (
                        <div className="relative group">
                          <img
                            src={firstPhoto}
                            alt={`${product.name} photo`}
                            className="w-12 h-12 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                            loading="lazy"
                            decoding="async"
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).onerror = null;
                              (e.currentTarget as HTMLImageElement).src = '/placeholder.svg';
                            }}
                            onClick={(e) => onImageClick(firstPhoto, e)}
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-50 rounded opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <ZoomIn className="w-4 h-4 text-white" />
                          </div>
                        </div>
                      ) : (
                        <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                          <Package className="w-6 h-6 text-gray-400" />
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div>
                      <div className="font-medium text-gray-900 text-sm">{product.name}</div>
                      {product.catalogName && product.catalogName !== product.name && (
                        <div className="text-xs text-green-600">POS: {product.catalogName}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                    {product.barcode}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {product.boxes}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                    {product.unitsPerBox}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1">
                      <Button
                        onClick={() => onEditProduct(product)}
                        variant="outline"
                        size="sm"
                        className="h-7"
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button
                        onClick={() => onDeleteProduct(product)}
                        variant="outline"
                        size="sm"
                        className="h-7 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
